import CloseIcon from "@mui/icons-material/Close";
import { Box, Typography } from "@mui/material";
import { type FC, type MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TooltipRenderProps } from "react-joyride";
import { FONT_SIZE, INK, SURFACE } from "../../../theme/tokens";
import { portalOverlayProps } from "../Popups/portalOverlay";
import SystemButton from "../SystemButton/SystemButton";
import {
	ANCHOR_ARRIVAL_MUTATIONS,
	anchorSelector,
	isAnchorVisible,
	revealTriggerWithin,
	scrollAnchorIntoView,
	sharesOpenSurface,
	whenAnchorSettles,
} from "./walkthroughAnchors";
import { WalkthroughTooltipGuideContext } from "./walkthroughGuideContext";
import { WalkthroughNextOpensContext } from "./walkthroughNextOpensContext";
import type { WalkthroughStepData } from "./walkthroughSteps";

/**
 * A walkthrough step's tooltip, supplied to react-joyride as `tooltipComponent`.
 *
 * A real component rather than vendor CSS overrides for two reasons: theme tokens and `SystemButton` by
 * construction, and `data-portal-overlay` so our outside-click handlers treat it as inside — without
 * that, a step over a dropdown tears the dropdown down.
 */
/** Wide enough for a two-line instruction, narrow enough not to cover the control it points at. */
const TOOLTIP_MAX_WIDTH_PX = 340;

/**
 * How long "open it first" waits before appearing, after the tour clicks the control itself.
 *
 * Longer than a normal reveal — a MUI transition is ~225ms and the settle wait a few frames on top — so
 * the message only ever renders when the click genuinely revealed nothing, which is the one case it is
 * written for. Short enough that a user whose click did nothing is not left guessing.
 */
const REVEAL_NUDGE_DELAY_MS = 600;

export const WalkthroughTooltip: FC<TooltipRenderProps> = ({ controls, index, size, isLastStep, step, backProps, primaryProps, skipProps, tooltipProps }) => {
	const { t } = useTranslation();
	const guide = useContext(WalkthroughTooltipGuideContext);
	const onNextOpens = useContext(WalkthroughNextOpensContext);
	const primaryRef = useRef<HTMLButtonElement>(null);
	// Keyed on the index rather than run once, so it survives either lifecycle. joyride keys its floater by
	// step index (`JoyrideStep-${index}`), so today this component really is remounted per step — measured,
	// 4 distinct tooltip nodes across a 4-step tour — but that is a vendor internal, not a contract.
	useEffect(() => {
		primaryRef.current?.focus({ preventScroll: true });
	}, [index]);

	// The step this one is supposed to reveal, if the next one is deferred. See `WalkthroughStepData`.
	const revealAnchor = (step.data as WalkthroughStepData | undefined)?.revealAnchor ?? null;
	// This step's own anchor: what Back compares surfaces against, and what the re-anchor watch follows.
	const ownAnchor = (step.data as WalkthroughStepData | undefined)?.anchor ?? null;
	const prevStepWasDeferred = (step.data as WalkthroughStepData | undefined)?.afterDeferred === true;
	const prevAnchor = (step.data as WalkthroughStepData | undefined)?.prevAnchor ?? null;
	// Read once per step: whether the previous control is still in the overlay this step stands in.
	const canGoBack = useMemo(
		() => index > 0 && (!prevStepWasDeferred || (prevAnchor !== null && ownAnchor !== null && sharesOpenSurface(prevAnchor, ownAnchor))),
		[index, prevStepWasDeferred, prevAnchor, ownAnchor],
	);
	// VISIBLE, not merely mounted. A panel rendered behind `display: none` satisfied a presence check,
	// so the Next handler below skipped the reveal and advanced into a step whose control was never opened.
	const isRevealed = useCallback((): boolean => revealAnchor !== null && isAnchorVisible(revealAnchor), [revealAnchor]);
	// Keyed on the index rather than a boolean so it clears itself when the step changes — and so a SECOND
	// Next on the same step goes through, which is the escape hatch for a user who means to skip.
	const [nudgedAt, setNudgedAt] = useState<number | null>(null);
	// One reveal click per step, so a second Next falls through to advancing rather than clicking again.
	const attemptedAtRef = useRef<number | null>(null);
	const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(
		() => () => {
			if (nudgeTimerRef.current !== null) clearTimeout(nudgeTimerRef.current);
		},
		[],
	);

	/**
	 * Re-anchor when this step's control is REPLACED under it, not merely moved.
	 *
	 * joyride binds the tooltip to the element it found as the step opened and re-measures only on scroll,
	 * resize or that element resizing, so a node React swaps out leaves the step pinned to a detached element
	 * at (0,0) — the call-off tour opened from another day's page caught that day's rows, which then gave way
	 * to today's. `open()` walks the step back through `tooltip_before`, which looks the target up again.
	 * Absent is not this watch's call: the vanish watch owns a control that is gone.
	 */
	useEffect(() => {
		if (ownAnchor === null) return;
		let current = document.querySelector(anchorSelector(ownAnchor));
		let cancelSettle: (() => void) | null = null;
		const observer = new MutationObserver(() => {
			const next = document.querySelector(anchorSelector(ownAnchor));
			if (next === null || next === current) return;
			current = next;
			cancelSettle?.();
			cancelSettle = whenAnchorSettles(ownAnchor, () => {
				void scrollAnchorIntoView(ownAnchor).then(() => controls.open());
			});
		});
		observer.observe(document.body, ANCHOR_ARRIVAL_MUTATIONS);
		return () => {
			observer.disconnect();
			cancelSettle?.();
		};
	}, [ownAnchor, controls]);

	const onPrimary = useCallback(
		(event: MouseEvent<HTMLElement>) => {
			// Already moved on — the reveal advanced first, or this is a second click on a step that is gone.
			if (controls.info().index !== index) return;
			// Next OPENS the thing rather than walking past it. The next step lives inside a control this one
			// points at, so "go to the next step" and "open this" are the same instruction — and joyride cannot
			// carry the user across the gap itself: while it polls for the missing target the overlay renders
			// with no spotlight cutout and its full-screen path still takes clicks, so the user is left on a
			// black screen unable to open the very thing being waited for.
			//
			// We click this step's OWN anchor, which `_schema.md` already requires to be the thing its copy
			// tells the user to click — never an element the tour picked for itself. It stays spotlit while it
			// happens, so the user still sees where the control was. `useWalkthroughStepAdvance` then advances.
			//
			// `nudgedAt` is the fallback for a click that reveals NOTHING (an anchor on a wrapper rather than
			// the control, say), and a second Next then goes through so nobody is trapped.
			//
			// Shown on a delay rather than up front. Setting it before the click used to be invisible only
			// because the advance was instant; now that the tour waits for the revealed control to settle,
			// React gets a paint in between and "open it first" flashed on every SUCCESSFUL transition —
			// caught in the browser on step 2 of the printout tour. The message has to wait long enough to
			// mean what it says.
			if (revealAnchor !== null && attemptedAtRef.current !== index && !isRevealed()) {
				attemptedAtRef.current = index;
				// Next is an explicit "open it", so it counts as the cause directly rather than relying on the
				// capture listener seeing the scripted click below — and it is the only cause that counts for a
				// target that arrives on its own.
				onNextOpens?.(index);
				// A marked trigger INSIDE the anchor wins, for an anchor that is a container rather than the
				// control itself — see `revealTriggerWithin`. Falls back to the anchor, which is the case
				// `_schema.md` describes and still the common one.
				if (typeof step.target === "string") (revealTriggerWithin(step.target) ?? document.querySelector<HTMLElement>(step.target))?.click();
				nudgeTimerRef.current = setTimeout(() => {
					if (!isRevealed()) setNudgedAt(index);
				}, REVEAL_NUDGE_DELAY_MS);
				return;
			}
			primaryProps.onClick(event);
		},
		[controls, revealAnchor, isRevealed, index, step.target, primaryProps.onClick, onNextOpens],
	);

	return (
		<Box
			{...tooltipProps}
			{...portalOverlayProps("walkthrough-tooltip")}
			ref={guide?.registerTooltip}
			aria-label={t("annie.walkthrough.aria_label")}
			data-testid="walkthrough-tooltip"
			sx={{ backgroundColor: SURFACE, borderRadius: 2, boxShadow: 6, maxWidth: TOOLTIP_MAX_WIDTH_PX, p: 2 }}
		>
			{guide?.slot}
			<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
				<Typography key={index} sx={{ color: INK.PRIMARY, flexGrow: 1, fontSize: FONT_SIZE.EMPHASIS }}>
					{step.content}
				</Typography>
				{/* `skipProps`, not `closeProps`: only skip ENDS the tour — v3's close advances a step. */}
				<SystemButton
					iconButton
					buttonVariant="borderless"
					leadingIcon={<CloseIcon fontSize="small" />}
					aria-label={t("annie.walkthrough.exit")}
					tooltip={t("annie.walkthrough.exit")}
					onClick={skipProps.onClick}
					data-testid="walkthrough-exit"
				/>
			</Box>
			{guide?.showWrongClickHint === true && (
				<Typography data-testid="walkthrough-wrong-click-hint" role="status" sx={{ color: INK.PRIMARY, fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, mt: 1 }}>
					{t("annie.walkthrough.wrong_click")}
				</Typography>
			)}
			{nudgedAt === index && (
				<Typography data-testid="walkthrough-reveal-nudge" sx={{ color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY, mt: 1 }}>
					{t("annie.walkthrough.reveal_first")}
				</Typography>
			)}
			<Box sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "space-between", mt: 2 }}>
				<Typography sx={{ color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY }}>{t("annie.walkthrough.progress", { current: index + 1, total: size })}</Typography>
				<Box sx={{ display: "flex", gap: 1 }}>
					{/* Back is hidden straight after a deferred step whose surface this step has left: going back
					    would target a control that no longer exists — joyride cannot acquire it, renders a
					    cutout-less overlay for its whole wait, then bounces forward again. Inside the SAME open
					    overlay (the removal dialog's steps) the previous control is still there, so Back stays. */}
					{canGoBack && (
						<SystemButton
							buttonVariant="borderless"
							size="small"
							onClick={backProps.onClick}
							aria-label={t("annie.walkthrough.back")}
							data-testid="walkthrough-back"
						>
							{t("annie.walkthrough.back")}
						</SystemButton>
					)}
					{/* Primary on the last step too — it becomes the tour's "last" action there. */}
					<SystemButton
						ref={primaryRef}
						buttonVariant="primary"
						size="small"
						onClick={onPrimary}
						aria-label={isLastStep ? t("annie.walkthrough.done") : t("annie.walkthrough.next")}
						data-testid="walkthrough-next"
					>
						{isLastStep ? t("annie.walkthrough.done") : t("annie.walkthrough.next")}
					</SystemButton>
				</Box>
			</Box>
		</Box>
	);
};
